import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import getCountryCodes from '../../translations/countryCodes';
import { getSellerShippingPreference } from '../../util/dataExtractors';
import {
  checkIfEnabledNonUSOrders,
  getOtherShippingPreferences,
  getOwnShippingPreference,
  getShippingDetails,
  getThresholdParams,
} from '../../util/userDataExtractor';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { createShippoAddress, selectShippoAddressStatus } from '../../ducks/shippo.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { OWN } from '../../util/constants';

import { DashboardLayout, H3, IconSpinner, Modal, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

import ShippingDetailsForm from './ShippingDetailsForm/ShippingDetailsForm';
import DimensionVerificationForm from './DimensionVerificationForm/DimensionVerificationForm';
import {
  saveWeightToAllListings,
  selectSaveWeightInProgress,
  selectSaveWeightSuccess,
} from './ShippingDetailsPage.duck';
import {
  buildShippingDetailsInitialValues,
  createShippingDetailsSubmitHandler,
} from './shippingDetails.helpers';

import css from './ShippingDetailsPage.module.css';

export const ShippingDetailsPageComponent = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const {
    currentUser,
    scrollingDisabled,
    updateInProgress,
    updateProfileError,
    saveShippoAddressInProgress,
    saveShippoAddressError,
    shippoAddressSaved,
    saveWeightInProgress,
    saveWeightSuccess,
    onUpdateProfile,
    onCreateShippoAddress,
    onRefreshCurrentUser = () => {},
    onSaveWeightToAllListings,
    onManageDisableScrolling,
  } = props;

  const [localPreference, setLocalPreference] = useState(null);
  const [showDimensionVerificationModal, setShowDimensionVerificationModal] = useState(false);

  const title = intl.formatMessage({ id: 'ShippingDetailsPage.title' });

  const shippingAddress = getShippingDetails(currentUser);
  const countryCodes = getCountryCodes(config.localization.locale);
  const savedCountry = countryCodes.find(c => c.code === shippingAddress?.country);

  const shippingPreference = getOwnShippingPreference(currentUser);
  const otherShippingPreferences = getOtherShippingPreferences(currentUser);
  const enableNonUSOrders = checkIfEnabledNonUSOrders(currentUser);
  const sellerShippingPreference = getSellerShippingPreference(currentUser);
  const thresholdParams = getThresholdParams(currentUser);
  const selectedShippingPreference = localPreference || shippingPreference;

  const initialValues = buildShippingDetailsInitialValues({
    shippingAddress: {
      ...shippingAddress,
      country: savedCountry?.code || shippingAddress?.country || 'US',
    },
    shippingPreference: selectedShippingPreference,
    shippingType: sellerShippingPreference,
    thresholdParams,
    otherShippingPreferences,
    enableNonUSOrders,
  });

  const handleSubmit = createShippingDetailsSubmitHandler({
    currentUser,
    onUpdateProfile,
    onCreateShippoAddress,
  });

  const onVerifyShippingPreferences = preference => {
    setShowDimensionVerificationModal(true);
    setLocalPreference(preference);
  };

  const dimensionVerificationModal = (
    <Modal
      id="ShippingDetailsPage.dimensionVerificationModal"
      isOpen={showDimensionVerificationModal}
      onClose={() => setShowDimensionVerificationModal(false)}
      onManageDisableScrolling={onManageDisableScrolling}
      modalTitle={intl.formatMessage({
        id: 'DimensionVerificationForm.confirmTitle',
      })}
    >
      <DimensionVerificationForm
        shippingPreference={selectedShippingPreference}
        inProgress={saveWeightInProgress}
        ready={saveWeightSuccess}
        onSubmit={values =>
          onSaveWeightToAllListings({
            ...values,
            shippingPreference: selectedShippingPreference,
            isOwn: selectedShippingPreference === OWN,
          })
        }
      />
    </Modal>
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <DashboardLayout
        topbar={<TopbarContainer isDashboardHeader={true} currentPage="ShippingDetailsPage" />}
      >
        <H3 as="h1" className={css.title}>
          <FormattedMessage id="ShippingDetailsPage.heading" />
        </H3>
        <div className={css.content}>
          {saveShippoAddressInProgress ? (
            <div className={css.spinner}>
              <IconSpinner />
            </div>
          ) : (
            <ShippingDetailsForm
              className={css.form}
              initialValues={initialValues}
              currentUser={currentUser}
              onSubmit={handleSubmit}
              onSaveVerifiedPhoneNumber={values => handleSubmit(values)}
              onManageDisableScrolling={onManageDisableScrolling}
              onRefreshCurrentUser={() => onRefreshCurrentUser()}
              onVerifyShippingPreferences={onVerifyShippingPreferences}
              inProgress={saveShippoAddressInProgress || updateInProgress}
              ready={shippoAddressSaved}
              updateProfileError={updateProfileError || saveShippoAddressError}
            />
          )}
        </div>
      </DashboardLayout>

      {dimensionVerificationModal}
    </Page>
  );
};

const mapStateToProps = state => {
  const shippoStatus = selectShippoAddressStatus(state);

  return {
    currentUser: state.user.currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    updateInProgress: state.ProfileSettingsPage?.updateInProgress,
    updateProfileError: state.ProfileSettingsPage?.updateProfileError,
    ...shippoStatus,
    saveWeightInProgress: selectSaveWeightInProgress(state),
    saveWeightSuccess: selectSaveWeightSuccess(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateProfile: params => dispatch(updateProfile(params)),
  onCreateShippoAddress: params => dispatch(createShippoAddress(params)),
  onRefreshCurrentUser: () => dispatch(fetchCurrentUser()),
  onSaveWeightToAllListings: params => dispatch(saveWeightToAllListings(params)),
});

const ShippingDetailsPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ShippingDetailsPageComponent);

export default ShippingDetailsPage;
